import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, ArrowRight, Bot, Check, ChevronDown, ChevronRight, ExternalLink, FileText, Flag, History, Link2, MoreHorizontal, Paperclip, Pin, PinOff, RefreshCw, Scale, Sparkles, Undo2, UserCheck, Wrench, XCircle } from "lucide-react";
import { useApi, type AiProposal, type CaseDocument } from "@ncp/api-client";
import { formatDate, formatDateTime, legalBasisText, useEnumLabel, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, SourceText, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, InlineAlert, Popover, PopoverContent, PopoverTrigger, QualityBadge, RichTextEditor, Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle, Tooltip, cn, copyText, keepEdited, toast, useSave, type RichTextEditorHandle } from "@ncp/ui";
import { ProposalList, RedraftAction } from "@ncp/assistant-sdk";
import { FINDING_VERDICTS, sameDirection, type AssessmentReference, type FindingVerdict, type ReviewCriterion, type ReviewFinding, type ReviewSource } from "./api";
import { AssessmentBody, ReferenceList } from "./AssessmentBody";
import { SuggestionChat } from "./SuggestionChat";
import { Kbd, SectionHeading, SourceIcon, VersionTag, checklistKeyOf, statusTone, submitKeys, verdictMeta, voiceMeta } from "./ui";
import { VersionHistoryDialog } from "./VersionHistoryDialog";
import { useStaffName } from "../../../lib/staff-names";
import { FigureInfo } from "../MethodInfo";
import { confidenceBandOf, useCalculationMethod } from "../figures";

interface CriterionCardProps {
  caseId: string;
  criterion: ReviewCriterion;
  index: number;
  total: number;
  readOnly: boolean;
  proposals: AiProposal[];
  agentName: (key: string) => string;
  onPrev: () => void;
  onNext: () => void;
  onOpenSource: (source: ReviewSource) => void;
  onOpenSurface: (surface: string) => void;
  onRaiseIssue: () => void;
  onOpenRun: (runId: string) => void;
  /** Opens the resolve dialog for a checklist item the assessment rests on (upload, re-file a document, or record a ruling). */
  onResolveChecklistItem?: (itemKey: string) => void;
  /** Bound by the parent so the whole pass can be keyed: returns the handler for a verdict hotkey / accept / note focus. */
  registerHotkeys: (handlers: { verdict: (v: Exclude<FindingVerdict, "Pending">) => void; acceptAi: () => void; focusNote: () => void }) => void;
  /** This criterion is the stage's next step, so its own action is the screen's one primary action. */
  primary?: boolean;
  /** The officer's view comes first (4.5, 7.12): the platform's assessment stays folded until the officer has recorded a finding or opens it. */
  officerFirst?: boolean;
}

/**
 * One criterion, read top to bottom. What the law asks (title, guidance, legal basis); then the officer's own finding, in gold; then – only
 * when the officer opens it on purpose – the platform's verdict and what it found, in sea blue and labelled as the platform's (3.1, 3.3, 4.5,
 * 7.12). Agreeing with the platform is a finding like any other: it records the officer's own reasons or evidence, never the platform's text.
 * Disagreeing must be explained. Every recorded finding is a new version, and the whole history is one click away.
 */
export function CriterionCard({ caseId, criterion: c, index, total, readOnly, proposals, agentName, onPrev, onNext, onOpenSource, onOpenSurface, onRaiseIssue, onOpenRun, onResolveChecklistItem, registerHotkeys, primary = false, officerFirst = true }: CriterionCardProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const enumLabel = useEnumLabel();
  const staffName = useStaffName();
  const api = useApi();
  const qc = useQueryClient();
  const ar = locale === "ar";
  const noteRef = useRef<RichTextEditorHandle>(null);

  const ai = c.ai ?? null;
  const finding = c.finding ?? null;
  const legalBasis = ar ? c.legalBasisAr ?? legalBasisText(c.legalBasis, locale) : c.legalBasis;
  const [verdict, setVerdict] = useState<FindingVerdict>(finding?.verdict ?? "Pending");
  const [note, setNote] = useState(finding?.note ?? "");
  const [evidence, setEvidence] = useState<ReviewSource[]>(finding?.evidence ?? []);
  const [history, setHistory] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  // Officer first (a platform setting): the platform's assessment stays folded until the officer has recorded a finding or opens it on purpose.
  // With the setting off it is open beside the officer's finding, and the officer may still fold it away.
  const openAtFirst = !officerFirst || (!!finding && finding.verdict !== "Pending");
  const [revealed, setRevealed] = useState(openAtFirst);
  const [agreeing, setAgreeing] = useState(false);
  useEffect(() => { setRevealed(openAtFirst); setAgreeing(false); }, [c.key]); // eslint-disable-line react-hooks/exhaustive-deps -- re-folds per criterion only
  // The sources fold stays as the officer left it while they move between criteria; the draft state re-syncs per criterion.
  const [sourcesOpen, setSourcesOpen] = useState(false);
  // Re-sync only when the officer moves to another criterion or a finding is (re)recorded — never on a background refetch, which
  // would wipe a note being typed while an agent run or another officer's action refreshes the review. A finding recorded meanwhile (by
  // another officer or an agent) brings its verdict, note and sources only into what this officer has not changed since it last came (S 2.7).
  const loadedFinding = useRef({ key: c.key, verdict, note, evidence });
  useEffect(() => {
    const next = { verdict: finding?.verdict ?? "Pending", note: finding?.note ?? "", evidence: finding?.evidence ?? [] };
    const last = loadedFinding.current;
    loadedFinding.current = { key: c.key, ...next };
    if (last.key !== c.key) { setVerdict(next.verdict); setNote(next.note); setEvidence(next.evidence); return; }
    setVerdict((v) => keepEdited(v, last.verdict, next.verdict));
    setNote((n) => keepEdited(n, last.note, next.note));
    setEvidence((e) => keepEdited(e, last.evidence, next.evidence));
  }, [c.key, finding?.reviewedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = () => { for (const k of [["case-review", caseId], ["case-review-history", caseId], ["finding-versions", caseId, c.key], ["case-events", caseId], ["case-audit", caseId]]) void qc.invalidateQueries({ queryKey: k }); };
  const save = useSave({
    mutationFn: (body: { verdict: FindingVerdict; note: string; evidence: ReviewSource[]; acceptAi?: boolean }) => api.put<ReviewFinding>(`/api/cases/${caseId}/review/findings/${encodeURIComponent(c.key)}`, { verdict: body.verdict, note: body.note || null, evidence: body.evidence, acceptAi: body.acceptAi ?? false, aiRevealed: revealed }),
    expect: "record",
    saved: (f, vars) => ({ title: vars.verdict === "Pending" ? t("backoffice:review.findingCleared") : t("backoffice:review.findingRecorded", { version: f.version }), description: vars.acceptAi ? t("backoffice:r3Process.criterion.agreed") : undefined }),
    onSuccess: () => { setAgreeing(false); invalidate(); },
  });
  const rerun = useSave({
    mutationFn: (agentKey: string) => api.post(`/api/ai/cases/${caseId}/run`, { agentKey }),
    onSuccess: (_, key) => toast.info(t("backoffice:review.refreshQueued"), { description: agentName(key) }),
  });

  const disagrees = verdict !== "Pending" && !!ai && ai.verdict !== "Unknown" && !sameDirection(ai.verdict, verdict);
  // Agreeing records the officer's own reasons (a sentence) or at least one piece of evidence (7.12).
  const agreesWithPlatform = agreeing && !!ai && verdict === ai.verdict;
  const agreementShort = agreesWithPlatform && note.trim().length < 10 && evidence.length === 0;
  const noteRequired = (disagrees && note.trim().length === 0) || agreementShort;
  const dirty = verdict !== (finding?.verdict ?? "Pending") || note.trim() !== (finding?.note ?? "").trim() || JSON.stringify(evidence) !== JSON.stringify(finding?.evidence ?? []);
  const recorded = !!finding && finding.verdict !== "Pending";

  /** Brings the response editor into reach without moving the page unless it is actually out of view. */
  const focusNote = () => { const editor = noteRef.current; if (!editor) return; editor.focus("end"); editor.element()?.scrollIntoView({ block: "nearest", behavior: "auto" }); };
  /**
   * Choosing a verdict is the officer's own finding, so it waits for their response: the cursor moves to the editor and ⌘↵ records.
   * Agreeing with the platform takes its verdict and waits, the same way, for the officer's reasons or evidence.
   */
  const choose = (v: Exclude<FindingVerdict, "Pending">) => {
    if (readOnly) return;
    setVerdict(v);
    setAgreeing(false);
    setTimeout(focusNote, 0);
  };
  const agree = () => {
    if (readOnly || !ai || ai.verdict === "Unknown") return;
    setVerdict(ai.verdict as FindingVerdict);
    setAgreeing(true);
    setTimeout(focusNote, 0);
  };
  /** Hotkey A: open the platform's assessment, then (pressed again) agree with it. */
  const acceptAi = () => { if (!revealed) setRevealed(true); else agree(); };
  const record = () => { if (verdict !== "Pending" && !noteRequired) save.mutate({ verdict, note, evidence, acceptAi: agreesWithPlatform }); };
  useEffect(() => { registerHotkeys({ verdict: choose, acceptAi, focusNote }); });

  /** A cited document opens in the workbench; law, Data Bank and tool references are read in their hover card. */
  const openReference = (r: AssessmentReference) => { if (r.sourceType === "Document") onOpenSource({ kind: "Document", id: r.sourceId, title: r.title, locator: r.locator ?? null }); };
  const pinned = (s: ReviewSource) => evidence.some((e) => e.kind === s.kind && e.id === s.id);
  const togglePin = (s: ReviewSource) => setEvidence((list) => (pinned(s) ? list.filter((e) => !(e.kind === s.kind && e.id === s.id)) : [...list, { kind: s.kind, id: s.id, title: s.title, locator: s.locator ?? null }]));
  const copyLink = async () => {
    const url = new URL(window.location.href); url.searchParams.delete("tab"); url.searchParams.set("criterion", c.key);
    await copyText(url.toString(), t("backoffice:review.linkCopied"));
  };

  const linkedProposal = useMemo(() => (ai?.proposalId ? proposals.find((p) => p.id === ai.proposalId) : undefined), [ai?.proposalId, proposals]);
  const title = ar ? c.titleAr : c.title;
  const guidance = ar ? c.guidanceAr : c.guidance;
  const surfaceTab = c.surface.split(":")[0];
  const surfaceLabel = t(`backoffice:review.surfaces.${surfaceTab}`, { defaultValue: surfaceTab });
  const sources = ai?.sources ?? [];
  const resolvable = (s: ReviewSource) => !readOnly && !!onResolveChecklistItem && checklistKeyOf(s.id) !== null;
  const toResolve = sources.filter((s) => checklistKeyOf(s.id) !== null && (s.status === "Missing" || s.status === "Deficient")).length;

  // Checklist statuses have a translation; every other source status is shown as the backend names it.
  const statusLabel = (status: string) => t(`backoffice:review.sourceStatus.${status}`, { defaultValue: t(`backoffice:workspace.completenessStatus.${status}`, { defaultValue: ar ? "" : status.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase() }) });
  // A source title or locator the platform wrote has its Arabic; a document's or a party's own title stays as they wrote it.
  const sourceTitle = (s: ReviewSource) => (ar && s.titleAr ? s.titleAr : s.title);
  const sourceLocator = (s: { locator?: string | null; locatorAr?: string | null }) => !s.locator ? null : !ar ? s.locator : s.locatorAr ?? legalBasisText(s.locator, locale) ?? (/\.[a-z0-9]{2,4}$/i.test(s.locator) ? s.locator : null);
  const pinnedTitle = (e: { kind: string; id: string; title: string }) => {
    const live = sources.find((s) => s.kind === e.kind && s.id === e.id);
    return ar && live?.titleAr ? live.titleAr : <SourceText text={e.title} />;
  };

  const AiVerdictIcon = ai ? verdictMeta[ai.verdict].icon : null;

  return (
    <Card className="overflow-hidden border-sand-200">
      {/* ------------------------------------------------------------ what the law asks */}
      <header className="p-5 pb-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="flex flex-wrap items-center gap-2 text-lg font-semibold leading-tight text-aeblack-900">{title}{!c.required ? <span className="rounded-full bg-sand-100 px-1.5 py-px text-[11px] font-medium text-sand-800">{t("backoffice:review.optional")}</span> : null}</h3>
            {guidance ? <p className="mt-1 text-sm leading-relaxed text-sand-800">{guidance}</p> : null}
            {legalBasis ? <p className="mt-1.5 flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground" title={legalBasis}><Scale className="size-3 shrink-0" /><span className="truncate" dir="auto">{legalBasis}</span></p> : null}
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            <Tooltip content={surfaceLabel}><Button size="icon-sm" variant="ghost" aria-label={surfaceLabel} onClick={() => onOpenSurface(c.surface)}><ExternalLink /></Button></Tooltip>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button size="icon-sm" variant="ghost" aria-label={t("backoffice:review.moreActions")}><MoreHorizontal /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onSelect={() => setHistory(true)}><History /> {t("backoffice:review.versions.view")}{finding?.version ? <span className="ms-auto font-mono text-[10px] text-muted-foreground">v{finding.version}</span> : null}</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void copyLink()}><Link2 /> {t("backoffice:review.copyLink")}</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onOpenSurface(c.surface)}><ExternalLink /> {surfaceLabel}</DropdownMenuItem>
                {!readOnly ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={onRaiseIssue}><Flag /> {t("backoffice:review.raiseIssue")}</DropdownMenuItem>
                    {recorded ? <DropdownMenuItem onSelect={() => save.mutate({ verdict: "Pending", note, evidence })}><Undo2 /> {t("backoffice:review.clearFinding")}</DropdownMenuItem> : null}
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* ------------------------------------------------------------ what the officer concludes */}
      <section className={cn("border-s-4 border-t border-sand-200 bg-white p-5", voiceMeta.officer.edge)} aria-label={t("backoffice:review.yourFinding")}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SectionHeading icon={UserCheck} tone="officer">{t("backoffice:review.yourFinding")}</SectionHeading>
          {finding?.reviewedAt ? (
            <button type="button" onClick={() => setHistory(true)} className="text-[11px] text-muted-foreground underline-offset-2 hover:underline">
              <VersionTag version={finding.version} /> {staffName(finding.reviewedByName)} · {formatDateTime(finding.reviewedAt, locale)}
            </button>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup" aria-label={t("backoffice:review.yourFinding")}>
          {FINDING_VERDICTS.map((v) => {
            const meta = verdictMeta[v.value];
            const Icon = meta.icon;
            const active = verdict === v.value;
            const aiSame = revealed && !!ai && ai.verdict === v.value;
            return (
              <Tooltip key={v.value} content={<span>{t(`backoffice:review.verdictHints.${v.value}`)}{aiSame ? ` · ${t("backoffice:review.aiAgreesHint")}` : ""}</span>}>
                <button type="button" role="radio" aria-checked={active} disabled={readOnly} onClick={() => choose(v.value)}
                  className={cn("flex items-center gap-2 rounded-xl border px-3 py-2 text-start text-sm transition-colors disabled:cursor-default", active ? meta.ring : "border-sand-200 bg-white hover:bg-sand-50 disabled:hover:bg-white")}>
                  <Icon className={cn("size-4 shrink-0", meta.tone)} />
                  <span className={cn("min-w-0 flex-1 leading-tight", active && "font-semibold")}>{t(`backoffice:review.verdicts.${v.value}`)}</span>
                  {aiSame ? <Bot className={cn("size-3.5 shrink-0", voiceMeta.ai.icon)} aria-label={t("backoffice:review.aiAgreesHint")} /> : null}
                </button>
              </Tooltip>
            );
          })}
        </div>

        {disagrees && revealed ? <InlineAlert tone="warning" className="mt-3" title={t("backoffice:review.disagreeTitle", { ai: t(`backoffice:review.verdicts.${ai!.verdict}`) })} /> : null}
        {agreementShort ? <p className="mt-3 text-xs text-warning-800">{t("backoffice:r3Process.criterion.agreeNeeds")}</p> : null}

        <div className="mt-3">
          {readOnly && !note ? <p className="rounded-xl border border-dashed border-sand-300 px-3 py-2 text-[11px] text-muted-foreground">{t("backoffice:review.noResponse")}</p> : (
            <RichTextEditor
              ref={noteRef}
              value={note}
              onChange={setNote}
              draft={`case:${caseId}:criterion:${c.key}:note`}
              readOnly={readOnly}
              toolbar="compact"
              minHeightClassName="min-h-24"
              placeholder={disagrees && revealed ? t("backoffice:review.notePlaceholderDisagree") : t("backoffice:review.notePlaceholder")}
              onSubmit={record}
              aria-label={t("backoffice:review.response")}
              aria-invalid={noteRequired}
              actions={!readOnly ? (
                <>
                  <AttachDocument caseId={caseId} pinned={pinned} onToggle={togglePin} />
                  {/* Rewording works on what the officer typed and nothing else (7.12): it is off while the finding is empty, and the
                      platform's assessment is not given to it, so no platform text can be drafted into the officer's finding. */}
                  <RedraftAction
                    getText={() => note}
                    onApply={(text) => noteRef.current?.setMarkdown(text)}
                    disabled={note.replace(/<[^>]*>/g, "").trim().length === 0}
                    context={{ caseId, kind: "The case officer's own finding on one criterion of a stage review", title: c.title, guidance: c.guidance, verdict: verdict !== "Pending" ? verdict : undefined }}
                  />
                </>
              ) : undefined}
            />
          )}
        </div>

        {evidence.length > 0 ? (
          <div className="mt-3">
            <p className="mb-1 text-xs font-medium text-sand-700">{t("backoffice:review.evidence")} ({evidence.length})</p>
            <ul className="divide-y divide-sand-100 rounded-xl border border-sand-200">
              {evidence.map((e) => (
                <li key={`${e.kind}:${e.id}`} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                  <SourceIcon kind={e.kind} />
                  <button type="button" className="min-w-0 flex-1 truncate text-start hover:underline" onClick={() => onOpenSource(e)}>{pinnedTitle(e)}</button>
                  {sourceLocator(e) ? <span className="shrink-0 text-sand-500">{sourceLocator(e)}</span> : null}
                  {!readOnly ? <button type="button" aria-label={t("backoffice:review.unpinEvidence")} onClick={() => togglePin(e)} className="grid size-6 shrink-0 place-items-center rounded-md text-sand-500 hover:bg-danger-50 hover:text-danger-600"><XCircle className="size-3.5" /></button> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-sand-100 pt-4">
          <Button size="sm" variant="ghost" onClick={onPrev} disabled={index === 0}><ArrowLeft className="rtl:rotate-180" /> {t("backoffice:review.previous")}</Button>
          {!readOnly ? (
            <>
              {dirty && recorded ? <span className="text-[11px] text-warning-800">{t("backoffice:review.unsavedChanges")}</span> : null}
              <Tooltip content={<span>{t("backoffice:review.hotkeyRecord")} <Kbd>{submitKeys}</Kbd></span>}>
                <Button variant="gold" primary={primary} className="ms-auto min-w-40" disabled={verdict === "Pending" || noteRequired || !dirty} loading={save.isPending} onClick={record}>
                  <Check /> {recorded ? t("backoffice:review.recordNewVersion", { version: (finding?.version ?? 0) + 1 }) : t("backoffice:review.recordFinding")}
                </Button>
              </Tooltip>
            </>
          ) : null}
          <Button size="sm" variant="ghost" className={readOnly ? "ms-auto" : ""} onClick={onNext} disabled={index >= total - 1}>{t("backoffice:review.next")} <ArrowRight className="rtl:rotate-180" /></Button>
        </div>
      </section>

      {/* ------------------------------------------------------------ what the platform found: opened on purpose, after the officer's own view */}
      <section className={cn("border-s-4 border-t border-sand-200", voiceMeta.ai.edge, revealed ? cn("p-5", voiceMeta.ai.surface) : "bg-white px-5 py-3")} aria-label={t("backoffice:review.aiAssessment")}>
        {!revealed ? (
          <button type="button" onClick={() => setRevealed(true)} aria-expanded={false} className="flex w-full items-center gap-2 text-start text-sm font-medium text-seablue-900 hover:underline">
            <Bot className={cn("size-4 shrink-0", voiceMeta.ai.icon)} /> {t("backoffice:r3Process.criterion.showPlatform")} <ChevronDown className="size-3.5 text-sand-500" />
          </button>
        ) : (
        <>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Tooltip content={ai ? `${t(`backoffice:review.basis.${ai.basis}`)} · ${ai.agentKey ? `${agentName(ai.agentKey)}${ai.assessedAt ? ` · ${t("backoffice:review.draftOf", { date: formatDate(ai.assessedAt, locale) })}` : ""}` : t("backoffice:review.liveFromFile")}` : t("backoffice:review.manualOnly")}>
            <span className="cursor-help"><SectionHeading icon={Bot} tone="ai">{t("backoffice:review.voice.ai")}</SectionHeading></span>
          </Tooltip>
          {ai && AiVerdictIcon ? (
            <>
              <span className="flex items-center gap-1.5 text-sm font-semibold text-aeblack-900"><AiVerdictIcon className={cn("size-4 shrink-0", verdictMeta[ai.verdict].tone)} />{t(`backoffice:review.verdicts.${ai.verdict}`)}</span>
              <ConfidenceMeter value={ai.confidence} basis={ai.readingBasis} />
              <span className="ms-auto flex items-center gap-1.5">
                {linkedProposal?.qualityScore != null ? <span className="inline-flex items-center gap-0.5"><QualityBadge score={linkedProposal.qualityScore} notes={linkedProposal.qualityNotes} /><FigureInfo measure="draftQuality" /></span> : null}
                {!readOnly && ai.agentKey ? (
                  <Tooltip content={t("backoffice:review.refreshHint", { agent: agentName(ai.agentKey) })}>
                    <Button size="icon-sm" variant="ghost" className="size-7" aria-label={t("backoffice:review.refresh")} loading={rerun.isPending} onClick={() => rerun.mutate(ai.agentKey!)}><RefreshCw className="size-3.5" /></Button>
                  </Tooltip>
                ) : null}
                <Button size="sm" variant="ghost" className="h-7 text-sand-600" onClick={() => setRevealed(false)}>{t("backoffice:r3Process.criterion.hidePlatform")}</Button>
              </span>
            </>
          ) : null}
        </div>
        {ai ? (
          <>
            <div className={cn("rounded-xl border bg-white px-5 py-4 shadow-xs", voiceMeta.ai.border)} dir="auto">
              <AssessmentBody markdown={ar && ai.summaryAr ? ai.summaryAr : ai.summary} references={ai.references} onOpenReference={openReference} />
              {ai.references?.length ? <ReferenceList references={ai.references} onOpen={openReference} /> : null}
            </div>
            {!readOnly && ai.verdict !== "Unknown" ? (
              <div className="mt-3 flex justify-end">
                <Button size="sm" variant="outline" className="border-seablue-200 text-seablue-900 hover:border-seablue-400 hover:bg-seablue-50" onClick={agree}>
                  <Check /> {t("backoffice:r3Process.criterion.agree")}
                </Button>
              </div>
            ) : null}
            {ai.flags.length ? <div className="mt-2 flex flex-wrap gap-1">{ai.flags.map((f) => <Badge key={f} size="sm" variant="warning"><AlertTriangle /> {enumLabel("guardrail", f)}</Badge>)}</div> : null}
            {ai.verdict !== "Unknown" ? <SuggestionChat caseId={caseId} criterionKey={c.key} references={ai.references} readOnly={readOnly} onOpenReference={openReference} /> : null}

            {sources.length ? (
              <div className={cn("mt-3 overflow-hidden rounded-xl border bg-white/80", voiceMeta.ai.border)}>
                <button type="button" onClick={() => setSourcesOpen((v) => !v)} aria-expanded={sourcesOpen} className="flex w-full items-center gap-2 px-3 py-2 text-start text-xs hover:bg-seablue-50/60">
                  <ChevronRight className={cn("size-3.5 shrink-0 text-sand-500 transition-transform rtl:rotate-180", sourcesOpen && "rotate-90 rtl:rotate-90")} />
                  <span className="font-medium text-sand-700">{t("backoffice:review.sources", { count: sources.length })}</span>
                  {toResolve > 0 ? <span className="ms-auto inline-flex items-center gap-1 rounded-full bg-danger-50 px-2 py-px text-[11px] font-medium text-danger-800"><Wrench className="size-3" /> {t("backoffice:review.sourcesToResolve", { count: toResolve })}</span> : null}
                </button>
                {sourcesOpen ? (
                  <ul className="divide-y divide-sand-100 border-t border-sand-100">
                    {sources.map((s) => {
                      const itemKey = checklistKeyOf(s.id);
                      return (
                        <li key={`${s.kind}:${s.id}:${s.locator ?? ""}`} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                          <SourceIcon kind={s.kind} />
                          <button type="button" onClick={() => onOpenSource(s)} className="min-w-0 flex-1 truncate text-start text-aeblack-900 underline-offset-2 hover:underline" title={sourceTitle(s)}>{ar && s.titleAr ? s.titleAr : <SourceText text={s.title} />}</button>
                          {sourceLocator(s) ? <span className="hidden max-w-40 shrink-0 truncate text-sand-500 sm:inline" title={sourceLocator(s)!}>{sourceLocator(s)}</span> : null}
                          {s.status && statusLabel(s.status) ? <span className={cn("shrink-0 rounded-sm px-1.5 py-px text-[11px] font-medium", statusTone(s.status))}>{statusLabel(s.status)}</span> : null}
                          {itemKey && resolvable(s) ? <Tooltip content={t("backoffice:review.resolveFromHere")}><button type="button" onClick={() => onResolveChecklistItem!(itemKey)} aria-label={t("backoffice:review.resolveFromHere")} className={cn("grid size-6 shrink-0 place-items-center rounded-md border", s.status === "Missing" || s.status === "Deficient" ? "border-danger-200 bg-danger-50 text-danger-700 hover:bg-danger-100" : "border-sand-200 text-sand-500 hover:bg-sand-50")}><Wrench className="size-3" /></button></Tooltip> : null}
                          {!readOnly ? <Tooltip content={pinned(s) ? t("backoffice:review.unpinEvidence") : t("backoffice:review.pinEvidence")}><button type="button" onClick={() => togglePin(s)} aria-pressed={pinned(s)} aria-label={pinned(s) ? t("backoffice:review.unpinEvidence") : t("backoffice:review.pinEvidence")} className={cn("grid size-6 shrink-0 place-items-center rounded-md border", pinned(s) ? "border-aegold-400 bg-aegold-100 text-aegold-900" : "border-sand-200 text-sand-500 hover:bg-sand-50")}>{pinned(s) ? <Pin className="size-3" /> : <PinOff className="size-3" />}</button></Tooltip> : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{t("backoffice:review.manualOnly")}</p>
        )}
        {linkedProposal && linkedProposal.status === "Proposed" ? (
          <button type="button" onClick={() => setDraftOpen(true)} className={cn("mt-3 flex w-full items-center gap-2 rounded-xl border bg-white/80 px-3 py-2 text-start text-xs hover:bg-seablue-50/60", voiceMeta.ai.border)}>
            <Sparkles className={cn("size-3.5 shrink-0", voiceMeta.ai.icon)} />
            <span className="min-w-0 flex-1 truncate"><span className="text-muted-foreground">{t("backoffice:review.proposalAwaiting")}:</span> <span className="font-medium text-aeblack-900">{ar && linkedProposal.titleAr ? linkedProposal.titleAr : linkedProposal.title}</span></span>
            <span className="shrink-0 font-medium text-aegold-800">{t("backoffice:stage.left.actions.Draft")}</span>
          </button>
        ) : null}
        </>
        )}
      </section>

      <VersionHistoryDialog caseId={caseId} criterion={c} open={history} onOpenChange={setHistory} readOnly={readOnly} onOpenSource={onOpenSource} />
      {linkedProposal ? (
        <Sheet open={draftOpen} onOpenChange={setDraftOpen}>
          <SheetContent width="max-w-2xl">
            <SheetHeader><SheetTitle>{t("backoffice:review.proposalAwaiting")}</SheetTitle></SheetHeader>
            <SheetBody>
              <ProposalList proposals={[linkedProposal]} defaultOpenSingle invalidate={[["case", caseId], ["case-proposals", caseId], ["case-review", caseId], ["case-workflow", caseId], ["proposals"], ["proposals-summary"]]} onOpenRun={(id) => { setDraftOpen(false); onOpenRun(id); }} />
            </SheetBody>
          </SheetContent>
        </Sheet>
      ) : null}
    </Card>
  );
}

/**
 * How sure the platform is of its verdict: a short bar and the percentage, coloured by the bands of the published calculation method
 * (condition 2.16) – neutral until the method has loaded – with the ⓘ of the measure beside it (not in a compact row, which is itself a button).
 */
export function ConfidenceMeter({ value, compact = false, basis }: { value: number | null | undefined; compact?: boolean; basis?: string | null }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const method = useCalculationMethod();
  if (value == null) return null;
  const pct = Math.round((value <= 1 ? value * 100 : value));
  const band = confidenceBandOf(pct, method.data?.bands);
  const tone = band === "High" ? "bg-success-500" : band === "Medium" ? "bg-warning-500" : band === "Low" ? "bg-danger-500" : "bg-sand-400";
  const text = band === "High" ? "text-success-800" : band === "Medium" ? "text-warning-800" : band === "Low" ? "text-danger-700" : "text-sand-700";
  const meter = (
    <Tooltip content={t("backoffice:review.confidenceHint", { pct })}>
      <span className={cn("inline-flex cursor-help items-center gap-1.5 rounded-full border border-sand-200 bg-white px-2 py-0.5", compact && "border-0 bg-transparent px-0 py-0")}>
        {!compact ? <span className="text-[10px] font-medium uppercase tracking-wider text-sand-600">{t("backoffice:review.confidence")}</span> : null}
        <span className={cn("relative h-1.5 overflow-hidden rounded-full bg-sand-200", compact ? "w-8" : "w-14")} aria-hidden><span className={cn("absolute inset-y-0 start-0 rounded-full", tone)} style={{ width: `${pct}%` }} /></span>
        <span className={cn("text-xs font-semibold tabular-nums", text)}>{pct}%</span>
      </span>
    </Tooltip>
  );
  // The basis the rule named and the one value the published method gives it (condition 2.16); an agent's reading names none.
  const reading = basis ? method.data?.readingConfidences?.find((r) => r.key === basis) : undefined;
  const inputs = reading ? [t("backoffice:review.readingBasis", { meaning: locale === "ar" ? reading.meaningAr : reading.meaningEn, pct: reading.percent })] : undefined;
  return compact ? meter : <span className="inline-flex items-center gap-0.5">{meter}<FigureInfo measure="criterionConfidence" inputs={inputs} /></span>;
}

/** Pins any document of the case file to the finding, so the record shows what the officer actually read. */
function AttachDocument({ caseId, pinned, onToggle }: { caseId: string; pinned: (s: ReviewSource) => boolean; onToggle: (s: ReviewSource) => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const [open, setOpen] = useState(false);
  const docs = useQuery({ queryKey: ["case-documents", caseId], queryFn: () => api.get<CaseDocument[]>(`/api/cases/${caseId}/documents`), enabled: open });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip content={t("backoffice:review.attachDocument")}>
        <PopoverTrigger asChild><Button size="icon-sm" variant="ghost" className="size-7" aria-label={t("backoffice:review.attachDocument")}><Paperclip className="size-3.5" /></Button></PopoverTrigger>
      </Tooltip>
      <PopoverContent align="end" className="w-80 p-2">
        <p className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:workspace.documents")}</p>
        <ul className="max-h-64 space-y-0.5 overflow-y-auto scrollbar-thin">
          {(docs.data ?? []).map((d) => {
            const s: ReviewSource = { kind: "Document", id: d.id, title: d.fileName, locator: d.checklistItemKey ?? null };
            const on = pinned(s);
            return <li key={d.id}><button type="button" onClick={() => onToggle(s)} className={cn("flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-start text-xs hover:bg-sand-50", on && "bg-aegold-50")}><FileText className="size-3.5 shrink-0 text-sand-600" /><span className="min-w-0 flex-1 truncate">{d.fileName}</span>{on ? <Check className="size-3.5 text-aegold-800" /> : null}</button></li>;
          })}
          {docs.data && docs.data.length === 0 ? <li className="px-2 py-1 text-xs text-muted-foreground">{t("backoffice:workspace.noDocuments")}</li> : null}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
